import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { apiClient, setTokenGetter } from '../api/client';

export interface AuthUser {
  sub: string;
  email: string;
  role:
    | 'Admin'
    | 'Organizer'
    | 'Delegate'
    | 'Attendee'
    // Spinoff six-role model (design §2.6)
    | 'PlatformAdmin'
    | 'PlatformUser'
    | 'CustomerSuperUser'
    | 'CustomerEditor'
    | 'CustomerUser';
  fullName?: string;
  preferredLanguage?: string;
  isBetaTester?: boolean;
  betaMessage?: string;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  mustChangePassword: boolean;
}

interface LoginResult {
  user: AuthUser | null;
  mustChangePassword: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  mustChangePassword: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  forceChangePassword: (newPassword: string) => Promise<void>;
  demoLogin: () => void;
  logout: () => void;
  accessToken: string | null;
  setUser: React.Dispatch<React.SetStateAction<AuthUser | null>>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function parseJwt(token: string): AuthUser | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    const payload = JSON.parse(jsonPayload);
    return {
      sub: payload.sub,
      email: payload.email,
      role: payload.role,
      fullName: payload.fullName,
    };
  } catch {
    return null;
  }
}

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  mustChangePassword?: boolean;
  isBetaTester?: boolean;
  betaMessage?: string;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    accessToken: null,
    refreshToken: null,
    user: null,
    mustChangePassword: false,
  });

  // Use a ref to keep the token getter synchronously up-to-date.
  // This avoids the race condition where child useEffects fire before
  // the parent's useEffect registers the new token getter.
  const tokenRef = useRef<string | null>(authState.accessToken);
  tokenRef.current = authState.accessToken;

  useEffect(() => {
    setTokenGetter(() => tokenRef.current);
  }, []); // Only register once — the ref always has the latest value

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    const data = await apiClient<LoginResponse>('/auth/login', {
      method: 'POST',
      body: { email, password },
    });

    const user = parseJwt(data.accessToken);
    const mustChangePassword = data.mustChangePassword ?? false;
    // Attach beta tester flag from login response
    if (user && data.isBetaTester) {
      user.isBetaTester = true;
      if (data.betaMessage) user.betaMessage = data.betaMessage;
    }
    setAuthState({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      user,
      mustChangePassword,
    });

    // Fetch extended profile (preferredLanguage) after login
    if (user && !mustChangePassword) {
      try {
        const profile = await apiClient<{ preferredLanguage?: string }>('/auth/me', {
          headers: { Authorization: `Bearer ${data.accessToken}` },
        });
        if (profile?.preferredLanguage) {
          setAuthState((prev) => ({
            ...prev,
            user: prev.user ? { ...prev.user, preferredLanguage: profile.preferredLanguage } : prev.user,
          }));
        }
      } catch {
        // Non-critical — proceed without preferredLanguage
      }
    }

    return { user, mustChangePassword };
  }, []);

  const logout = useCallback(() => {
    if (authState.refreshToken) {
      apiClient('/auth/logout', {
        method: 'POST',
        body: { refreshToken: authState.refreshToken },
      }).catch(() => {
        // Ignore logout API errors
      });
    }
    setAuthState({ accessToken: null, refreshToken: null, user: null, mustChangePassword: false });
  }, [authState.refreshToken]);

  const demoLogin = useCallback(() => {
    const demoUser: AuthUser = {
      sub: 'demo-organizer-001',
      email: 'organizer@kdu-georgia.org',
      role: 'Organizer',
    };
    setAuthState({
      accessToken: 'demo-token',
      refreshToken: 'demo-refresh',
      user: demoUser,
      mustChangePassword: false,
    });
  }, []);

  const forceChangePassword = useCallback(async (newPassword: string) => {
    const data = await apiClient<LoginResponse>('/auth/force-change-password', {
      method: 'POST',
      body: { newPassword },
    });

    const user = parseJwt(data.accessToken);
    setAuthState({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      user,
      mustChangePassword: false,
    });
  }, []);

  const value: AuthContextValue = {
    user: authState.user,
    isAuthenticated: !!authState.accessToken,
    mustChangePassword: authState.mustChangePassword,
    login,
    forceChangePassword,
    demoLogin,
    logout,
    accessToken: authState.accessToken,
    setUser: (updater) => {
      setAuthState((prev) => ({
        ...prev,
        user: typeof updater === 'function' ? updater(prev.user) : updater,
      }));
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
